import { FormContainer } from '@if/mui'
import { useGetMerchantFinancingProgramProviders } from '@src/data/api/merchants-api/merchants-api'
import {
  useCreateMechanicalWarrantyWorksheet,
  useEditMechanicalWarrantyWorksheet,
} from '@src/data/api/worksheet-api/mechanical-warranty-worksheet-api'
import { CreditApplication } from '@src/types/CreditApplicationSchema'
import { buildMechanicalWarrantyWorksheetSchema, MechanicalWarrantyWorksheet } from '@src/types/WorksheetSchema'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import WorksheetResetAction from '../components/WorksheetResetAction'
import WorksheetSkeleton from '../components/WorksheetSkeleton'
import MechanicalWarrantyWorksheetBody from './components/MechanicalWarrantyWorksheetBody'

interface Props {
  creditApplication: CreditApplication
}

const MechanicalWarrantyWorksheetPage = ({ creditApplication }: Props) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const MechanicalWarrantyWorksheetSchema = buildMechanicalWarrantyWorksheetSchema(creditApplication)
  // Cast the loaded worksheet through the schema so its wire-string dates become
  // `DateOnly` (what the pickers require); the cast backfills any missing field
  // with its schema default, same as the create branch's `getDefault()`.
  const defaultValues = (creditApplication.worksheet
    ? MechanicalWarrantyWorksheetSchema.cast(creditApplication.worksheet)
    : MechanicalWarrantyWorksheetSchema.getDefault()) as unknown as MechanicalWarrantyWorksheet

  const [createWorksheet] = useCreateMechanicalWarrantyWorksheet()
  const [editWorksheet] = useEditMechanicalWarrantyWorksheet()
  const [merchantMechanicalWarrantyProviders, isLoadingProviders] = useGetMerchantFinancingProgramProviders(
    creditApplication?.merchantId ?? '',
    creditApplication?.financingProgramId ?? '',
  )
  const [fundingReady, setFundingReady] = useState(false)

  const saveWorksheet = async (data: MechanicalWarrantyWorksheet) => {
    if (!fundingReady) {
      return false
    }
    data.creditApplicationId = creditApplication.id
    if (creditApplication.worksheet) {
      await editWorksheet({ worksheet: data })
    } else {
      await createWorksheet({ worksheet: data })
    }
    navigate(`/credits/${creditApplication.financingProgramId}/${creditApplication.id}`, { replace: true })
  }

  if (isLoadingProviders || !merchantMechanicalWarrantyProviders?.length) {
    return <WorksheetSkeleton />
  }

  return (
    <>
      <FormContainer
        mode={creditApplication.worksheet ? 'edit' : 'create'}
        useFormSetup={() => ({ schema: MechanicalWarrantyWorksheetSchema, defaultValues })}
        onSubmit={saveWorksheet}
        successMessage={t('worksheet.saveSuccess')}
        pageActions
        submitLabel={t('worksheet.createContract')}
        actions={<WorksheetResetAction />}
      >
        <MechanicalWarrantyWorksheetBody
          creditApplication={creditApplication}
          merchantMechanicalWarrantyProviders={merchantMechanicalWarrantyProviders}
          onFundingReadyChange={setFundingReady}
        />
      </FormContainer>
    </>
  )
}

export default MechanicalWarrantyWorksheetPage
